import _taggedTemplateLiteral from "@babel/runtime/helpers/esm/taggedTemplateLiteral";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";

var _templateObject, _templateObject2;

var _excluded = ["alignTooltip", "autoTooltip", "children", "disableTooltip", "disabled", "error", "id", "isOpen", "label", "labelProps", "name", "success", "tooltip", "tooltipProps", "warning", "onBlur", "onFocus"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { iconSize, space } from '@repay/cactus-theme';
import PropTypes from 'prop-types';
import React from 'react';
import { margin, width } from 'styled-system';
import { FieldWrapper } from '../FieldWrapper/FieldWrapper';
import { Flex } from '../Flex/Flex';
import { isFocusOut } from '../helpers/events';
import { omitProps } from '../helpers/omit';
import { flexItem, styledUnpoly, styledWithClass } from '../helpers/styled';
import useId from '../helpers/useId';
import Label from '../Label/Label';
import StatusMessage from '../StatusMessage/StatusMessage';
import { Tooltip } from '../Tooltip/Tooltip'; // These are the props commonly used by components that wrap AccessibleField.

export function useAccessibleField(_ref) {
  var id = _ref.id,
      name = _ref.name,
      error = _ref.error,
      warning = _ref.warning,
      success = _ref.success,
      disabled = _ref.disabled,
      tooltip = _ref.tooltip;
  var fieldId = useId(id, name);
  var labelId = "".concat(fieldId, "-label");
  var statusId = "".concat(fieldId, "-status");
  var tooltipId = "".concat(fieldId, "-tip");
  var status;
  var statusMessage;

  if (error) {
    status = 'error';
    statusMessage = error;
  } else if (warning) {
    status = 'warning';
    statusMessage = warning;
  } else if (success) {
    status = 'success';
    statusMessage = success;
  }

  var describedByIds = [tooltip && tooltipId, status && statusId].filter(Boolean);
  return {
    fieldId: fieldId,
    ariaDescribedBy: describedByIds.join(' ') || undefined,
    labelId: labelId,
    statusId: statusId,
    name: name || '',
    status: status,
    statusMessage: statusMessage,
    tooltipId: tooltipId,
    disabled: disabled
  };
}

function AccessibleFieldBase(props) {
  var _rest$role;

  var _props$alignTooltip = props.alignTooltip,
      alignTooltip = _props$alignTooltip === void 0 ? 'right' : _props$alignTooltip,
      _props$autoTooltip = props.autoTooltip,
      autoTooltip = _props$autoTooltip === void 0 ? true : _props$autoTooltip,
      children = props.children,
      disableTooltip = props.disableTooltip,
      disabled = props.disabled,
      error = props.error,
      id = props.id,
      isOpen = props.isOpen,
      label = props.label,
      labelProps = props.labelProps,
      name = props.name,
      success = props.success,
      tooltip = props.tooltip,
      tooltipProps = props.tooltipProps,
      warning = props.warning,
      onBlur = props.onBlur,
      onFocus = props.onFocus,
      rest = _objectWithoutProperties(props, _excluded);

  var hookArgs = {
    id: id,
    name: name,
    tooltip: tooltip,
    disabled: disabled,
    error: error,
    warning: warning,
    success: success
  };
  var accessibility = useAccessibleField(hookArgs);
  var fieldId = accessibility.fieldId,
      ariaDescribedBy = accessibility.ariaDescribedBy,
      labelId = accessibility.labelId,
      statusId = accessibility.statusId,
      tooltipId = accessibility.tooltipId,
      status = accessibility.status,
      statusMessage = accessibility.statusMessage;
  var ref = React.useRef(null);

  var _React$useState = React.useState(false),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      forceTooltipVisible = _React$useState2[0],
      setTooltipVisible = _React$useState2[1];

  var _React$useState3 = React.useState(undefined),
      _React$useState4 = _slicedToArray(_React$useState3, 2),
      maxWidth = _React$useState4[0],
      setMaxWidth = _React$useState4[1];

  React.useLayoutEffect(function () {
    if (ref.current instanceof HTMLElement) {
      var containerWidth = "".concat(ref.current.getBoundingClientRect().width - 32, "px");
      setMaxWidth(containerWidth);
    }
  }, []);

  var handleFieldBlur = function handleFieldBlur(e) {
    onBlur === null || onBlur === void 0 ? void 0 : onBlur(e);

    if (autoTooltip && isFocusOut(e)) {
      setTooltipVisible(false);
    }
  };

  var handleFieldFocus = function handleFieldFocus(e) {
    onFocus === null || onFocus === void 0 ? void 0 : onFocus(e);

    if (autoTooltip) {
      setTooltipVisible(true);
    }
  }; // If this represents a group of fields, the label points to the group instead of the child.


  if ((_rest$role = rest.role) !== null && _rest$role !== void 0 && _rest$role.includes('group')) {
    // @ts-ignore
    rest.id = fieldId;
    rest['aria-labelledby'] = labelId;
    rest['aria-describedby'] = ariaDescribedBy;
  }

  var $labelProps = _objectSpread(_objectSpread({}, labelProps), {}, {
    id: labelId,
    htmlFor: fieldId
  });

  if (disabled) {
    delete $labelProps.colors;
    $labelProps.color = 'mediumGray';
  }

  return /*#__PURE__*/React.createElement("div", Object.assign({}, rest, {
    ref: ref,
    onFocus: handleFieldFocus,
    onBlur: handleFieldBlur
  }), /*#__PURE__*/React.createElement(Flex, {
    className: "field-label-row",
    flexWrap: "nowrap",
    justifyContent: alignTooltip === 'right' ? 'space-between' : 'flex-start',
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(FieldLabel, $labelProps, label), tooltip && /*#__PURE__*/React.createElement(Tooltip, Object.assign({}, tooltipProps, {
    label: tooltip,
    id: tooltipId,
    maxWidth: maxWidth,
    disabled: disableTooltip !== null && disableTooltip !== void 0 ? disableTooltip : disabled,
    forceVisible: !isOpen && forceTooltipVisible
  }))), typeof children === 'function' ? children(accessibility) : /*#__PURE__*/React.cloneElement(React.Children.only(children), {
    id: fieldId,
    name: name,
    'aria-describedby': ariaDescribedBy,
    status: status,
    disabled: disabled
  }), status !== undefined && /*#__PURE__*/React.createElement("div", {
    className: "field-status-row"
  }, /*#__PURE__*/React.createElement(StatusMessage, {
    status: status,
    id: statusId
  }, statusMessage)));
}

AccessibleFieldBase.displayName = 'AccessibleField';
export var AccessibleField = styledUnpoly(FieldWrapper, AccessibleFieldBase).withConfig(omitProps(width, margin, flexItem))(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  position: relative;\n  ", "\n  display: flex;\n  flex-direction: column;\n\n  .field-label-row ", " {\n    position: relative;\n    font-size: ", ";\n    margin: 0 ", ";\n  }\n\n  .field-status-row ", " {\n    margin-top: ", ";\n  }\n"])), width, Tooltip, iconSize('small'), space(3), StatusMessage, space(2));
var FieldLabel = styledWithClass(Label, 'field-label')(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n  display: block;\n  box-sizing: border-box;\n  min-width: 1px;\n  overflow-wrap: break-word;\n  word-wrap: break-word;\n  :first-child {\n    margin: 0 ", ";\n  }\n  :not(:last-child) {\n    margin-right: 0;\n  }\n"])), space(4));
FieldLabel.displayName = 'AccessibleField.Label';
AccessibleField.propTypes = {
  label: PropTypes.node.isRequired,
  labelProps: PropTypes.object,
  name: PropTypes.string.isRequired,
  className: PropTypes.string,
  id: PropTypes.string,
  success: PropTypes.node,
  warning: PropTypes.node,
  error: PropTypes.node,
  tooltip: PropTypes.node,
  tooltipProps: PropTypes.object,
  disabled: PropTypes.bool,
  autoTooltip: PropTypes.bool,
  disableTooltip: PropTypes.bool
};
export default AccessibleField;